<template>
  <div class="tw-rounded-2xl tw-border tw-border-slate-200/70 tw-bg-white/60 tw-backdrop-blur tw-shadow-sm tw-shadow-slate-900/5 tw-p-4">
    <div class="tw-flex tw-items-start tw-justify-between tw-gap-3">
      <div class="tw-flex tw-items-center tw-gap-2">
        <font-awesome-icon icon="location-dot" class="tw-h-4 tw-w-4 tw-text-slate-500" />
        <h4 class="tw-text-base tw-font-semibold tw-text-slate-900">
          {{ isExportOrder ? 'Drop-Off Terminal' : 'Pick-Up Terminal' }}
        </h4>
        <CopyButton
          v-if="locationCopyValue"
          :value="locationCopyValue"
          :title="isExportOrder ? 'Copy drop-off location' : 'Copy pick-up location'"
          :ariaLabel="isExportOrder ? 'Copy drop-off location' : 'Copy pick-up location'"
        />
      </div>

      <div class="tw-flex tw-items-center tw-gap-2">
        <button
          v-if="editable"
          type="button"
          class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-slate-200/70 tw-bg-white/70 tw-px-3 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-white"
          title="Edit terminal"
          aria-label="Edit terminal"
          @click="toggleEditingTerminal"
        >
          <font-awesome-icon icon="pen-to-square" />
        </button>
      </div>
    </div>

    

    <div v-if="order.pickUpLocation" class="tw-mt-3 tw-text-sm tw-text-slate-700">
      <div class="tw-font-medium tw-text-slate-900">{{ order.pickUpLocation.name }}</div>
      <div v-if="order.pickUpLocation.address">{{ addressLine2(order.pickUpLocation.address) }}</div>
      <div>{{ addressLine3(order.pickUpLocation.address) }}</div>
    </div>

    <div v-else-if="order.billOfLading && order.billOfLading.pickUpLocation" class="tw-mt-3 tw-text-sm tw-text-slate-700">
      {{ order.billOfLading.pickUpLocation }}
    </div>

    <div v-if="isEditingTerminal" class="tw-mt-3 tw-flex tw-w-full tw-items-center tw-gap-2">
      <div class="tw-min-w-0 tw-flex-1">
        <FilterableDropdown
          class="tw-w-full tw-min-w-0"
          placeholder="Select terminal"
          :options="terminals"
          :customLabel="(x) => (x.shortName ? x.shortName : x.name)"
          v-model="selectedTerminal"
        />
      </div>

      <div
        class="tw-inline-flex tw-h-10 tw-shrink-0 tw-overflow-hidden tw-rounded-full tw-border tw-border-slate-200/70 tw-bg-white/70"
      >
        <button
          type="button"
          class="tw-inline-flex tw-h-10 tw-items-center tw-justify-center tw-px-3 tw-text-slate-700 hover:tw-bg-white disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
          :disabled="savingTerminal || !selectedTerminal?.id"
          title="Save"
          aria-label="Save"
          @click="saveTerminal"
        >
          <font-awesome-icon v-if="savingTerminal" icon="circle-notch" spin />
          <font-awesome-icon v-else icon="check" />
        </button>
        <div class="tw-w-px tw-bg-slate-200/70"></div>
        <button
          type="button"
          class="tw-inline-flex tw-h-10 tw-items-center tw-justify-center tw-px-3 tw-text-slate-700 hover:tw-bg-white"
          title="Cancel"
          aria-label="Cancel"
          @click="cancelEditTerminal"
        >
          <font-awesome-icon icon="xmark" />
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { addressMixin } from '../../../utils/address';
import { pushToast } from '@/lib/toast';

import CopyButton from '@/components/core/CopyButton.vue';
import FilterableDropdown from '@/components/core/FilterableDropdown.vue';
import TerminalsQuery from '@/gql/terminals/Terminals.gql';
import EditOrderTerminalMutation from '@/gql/orders/mutations/EditOrderTerminal.gql';

export default defineComponent({
  emits: ['updateOrder'],
  props: {
    order: {
      type: Object,
      required: true,
    },
    isExportOrder: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: true,
    },
  },
  mixins: [addressMixin],
  components: {
    CopyButton,
    FilterableDropdown,
  },
  data(): any {
    return {
      isEditingTerminal: false,
      selectedTerminal: null as any,
      terminals: [],
      savingTerminal: false,
    };
  },
  computed: {
    locationCopyValue(): string {
      const vm = this as any;
      const order = vm.order;

      if (order?.pickUpLocation) {
        const name = order.pickUpLocation.name || '';
        const line2 = order.pickUpLocation.address ? vm.addressLine2(order.pickUpLocation.address) : '';
        const line3 = order.pickUpLocation.address ? vm.addressLine3(order.pickUpLocation.address) : '';
        return [name, line2, line3].filter(Boolean).join('\n');
      }

      if (order?.billOfLading?.pickUpLocation) {
        return String(order.billOfLading.pickUpLocation);
      }

      return '';
    },
  },
  methods: {
    toggleEditingTerminal() {
      const vm = this as any;
      if (vm.isEditingTerminal) {
        vm.isEditingTerminal = false;
        vm.selectedTerminal = null;
        return;
      }
      vm.selectedTerminal = vm.order?.pickUpLocation || null;
      vm.isEditingTerminal = true;
    },
    cancelEditTerminal() {
      const vm = this as any;
      vm.isEditingTerminal = false;
      vm.selectedTerminal = null;
    },
    saveTerminal() {
      const vm = this as any;
      if (!vm.selectedTerminal?.id) return;

      vm.savingTerminal = true;
      vm.$apollo
        .mutate({
          mutation: EditOrderTerminalMutation,
          variables: {
            orderId: Number(vm.order.id),
            terminalId: Number(vm.selectedTerminal.id),
          },
        })
        .then(() => {
          vm.savingTerminal = false;
          vm.isEditingTerminal = false;
          pushToast('Terminal updated successfully', 'success', 3000);
          vm.$emit('updateOrder');
        })
        .catch((err) => {
          vm.savingTerminal = false;
          console.error(err);
          pushToast('Could not update terminal', 'error', 4000);
        });
    },
  },
  apollo: {
    terminals: {
      query: TerminalsQuery,
      update: (data: any) => data.terminals || [],
    },
  },
});
</script>
